/** @jsx jsx */
import { css, jsx, keyframes } from '@emotion/react';

import * as React from 'react';
import { useEffect, useRef } from 'react';

import type { Theme } from '@coursera/cds-core';
import { Grid, PageGridContainer, Typography2 } from '@coursera/cds-core';

import PageNavigation from 'bundles/unified-description-page-common/components/shared/PageNavigation';
import type { NavigationLink } from 'bundles/unified-description-page-common/components/shared/PageNavigation';
import { FIXED_PAGE_NAVIGATION_Z_INDEX } from 'bundles/unified-description-page-common/constants/index';
import { usePageNavigation } from 'bundles/unified-description-page-common/contexts/PageNavigationContext';

const animateIn = keyframes`
  0% {
    opacity: 0;
    transform: translateY(-200px);
  }
  100% {
    opacity: 1;
    transform: translateY(0px);
  }
`;

type Props = {
  productTitle: string;
  navigationLinks?: Array<NavigationLink>;
  pageCta?: React.ReactNode;
  partnerLogo?: React.ReactNode;
};

const styles = {
  navContainer: (theme: Theme) => css`
    position: fixed;
    background: var(--cds-color-white-0);
    width: 100%;
    opacity: 0;
    transform: translateY(-200px);
    z-index: ${FIXED_PAGE_NAVIGATION_Z_INDEX};
    top: 0;
    display: none;
    box-shadow: 0 2px 12px rgb(0 0 0 / 24%);
  `,
  slideIn: css`
    display: block;
    animation: 0.5s ease-in-out 1 normal forwards running ${animateIn};
  `,
  header: (theme: Theme) => css`
    border-bottom: 2px solid ${theme.palette.gray[300]};
    padding: ${theme.spacing(8, 0)};
    display: flex;
    gap: ${theme.spacing(32)};
    justify-content: space-between;
    align-items: center;
    ${theme.breakpoints.down('sm')} {
      display: none;
    }
  `,
  titleContainer: (theme: Theme) => css`
    display: flex;
    gap: ${theme.spacing(24)};
    align-items: center;
    flex: 1;
    min-width: 0;
  `,
  title: css`
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  `,
  pageCtaContainer: (showPageCtaInNav: boolean) => css`
    visibility: ${showPageCtaInNav ? 'visible' : 'hidden'};
    opacity: ${showPageCtaInNav ? 1 : 0};
    transition: opacity 0.3s linear;
    min-width: 180px;
  `,
};

const FixedPageNavigation = ({ productTitle, navigationLinks, pageCta, partnerLogo }: Props) => {
  const { showFixedPageNav, showPageCtaInNav, updateFixedPageNavHeight } = usePageNavigation();
  const navRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (showFixedPageNav) {
      const navHeight = navRef.current?.getBoundingClientRect().height;

      if (navHeight && updateFixedPageNavHeight) {
        updateFixedPageNavHeight(navHeight);
      }
    }
  }, [updateFixedPageNavHeight, showFixedPageNav]);

  if (!productTitle) {
    return null;
  }

  return (
    <div
      data-e2e="fixed-page-navigation"
      css={[styles.navContainer, showFixedPageNav ? styles.slideIn : undefined]}
      ref={navRef}
    >
      <PageGridContainer>
        <Grid container item xs={12}>
          <Grid item xs={12}>
            <div css={styles.header}>
              <div css={styles.titleContainer}>
                {partnerLogo}
                <Typography2 variant="subtitleMedium" css={styles.title} component="div">
                  {productTitle}
                </Typography2>
              </div>
              {showPageCtaInNav && <div css={styles.pageCtaContainer(showPageCtaInNav)}>{pageCta}</div>}
            </div>
            <PageNavigation navigationLinks={navigationLinks} />
          </Grid>
        </Grid>
      </PageGridContainer>
    </div>
  );
};

export default FixedPageNavigation;
